import axios from "axios";
import CookieUtils from "../../../utils/CookieUtils";
import Dispatcher from "../../../Dispatcher";
import JwtUtils from "../../../utils/JwtUtils";
import LoadObject from "../../load_objects/LoadObject";
import LoadObjectState from "../../load_objects/LoadObjectState";
import LoginDataManager from "../dataManagers/LoginDataManager";
import { Action } from "../Actions";
import { Authenticator } from "../../../components/security";
import { ILogin, Login } from "../records/Login";
import { ReduceStore } from "flux/utils";

type IState = ILogin;

const emptyState: IState = {
    token: undefined,
    tokenPayload: undefined,
    isLoggedIn: false,
    username: undefined,
    passwordChanged: false
};

class LoginStore extends ReduceStore<LoadObjectState<IState>, Action>
{
    constructor()
    {
        super(Dispatcher);
    }

    public getInitialState(): LoadObjectState<IState>
    {
        return new LoadObjectState<IState>(() => Dispatcher.dispatch({
            type: "login/start-load"
        }));
    }

    public getStateSafely(): IState
    {
        return this.unwrap(super.getState());
    }

    public reduce(stateLoader: LoadObjectState<IState>, action: Action): LoadObjectState<IState>
    {
        const state = this.unwrap(stateLoader);

        switch (action.type)
        {
            case "login/start-load":
                {
                    const cookie = CookieUtils.getCookie("GameScorekeeper");

                    if (cookie)
                    {
                        const authStr = "Bearer " + cookie;
                        axios.defaults.headers.common.Authorization = authStr;

                        const jwtPayload = JwtUtils.getJWTPayload(cookie);
                        const username = jwtPayload.sub;

                        const login = {
                            token: cookie,
                            tokenPayload: jwtPayload,
                            isLoggedIn: true,
                            username,
                            passwordChanged: false
                        };
                        Authenticator.update(login);
                        return this.wrap(
                            new LoadObjectState<IState>(() => { }),
                            login);
                    }
                    else
                    {
                        Authenticator.update(undefined);
                        return this.wrap(new LoadObjectState<IState>(() => { }), emptyState);
                    }
                }

            case "login/login":
                {
                    const username = action.username;
                    const pwd = action.pwd;

                    if (!username || !pwd)
                    {
                        return stateLoader;
                    }

                    LoginDataManager.login(username, pwd);

                    return stateLoader.setLoadObject(LoadObject.loading<IState>());
                }

            case "login/loaded":
                {
                    const token = action.token;
                    const authStr = "Bearer " + token;
                    axios.defaults.headers.common.Authorization = authStr;

                    const jwtPayload = JwtUtils.getJWTPayload(token);
                    const username = jwtPayload.sub;

                    CookieUtils.saveCookie("GameScorekeeper", token, 7);

                    const login = {
                        token,
                        tokenPayload: jwtPayload,
                        isLoggedIn: true,
                        username,
                        passwordChanged: false
                    };
                    Authenticator.update(login);
                    return this.wrap(stateLoader, login);
                }

            case "login/error":
                {
                    return stateLoader.setLoadObject(LoadObject.withError(action.error));
                }

            case "login/revoke-token":
                {
                    const username = action.username;
                    const pwd = action.pwd;

                    if (!username || !pwd)
                    {
                        return stateLoader;
                    }

                    LoginDataManager.revokeToken(username, pwd);

                    return stateLoader;
                }

            case "login/revoke-token/fulfilled":
                {
                    const token = action.token;
                    const authStr = "Bearer " + token;
                    axios.defaults.headers.common.Authorization = authStr;

                    const jwtPayload = JwtUtils.getJWTPayload(token);
                    const username = jwtPayload.sub;

                    CookieUtils.saveCookie("GameScorekeeper", token, 7);
                    return this.wrap(
                        stateLoader,
                        {
                            token,
                            tokenPayload: JwtUtils.getJWTPayload(token),
                            isLoggedIn: true,
                            username,
                            passwordChanged: false
                        });
                }

            case "login/revoke-token/error":
                {
                    return stateLoader.setLoadObject(stateLoader.getLoadObject().setError(action.error));
                }

            case "logout":
                {
                    axios.defaults.headers.common.Authorization = "";
                    CookieUtils.deleteCookie("GameScorekeeper");

                    Authenticator.update(undefined);
                    return this.wrap(
                        stateLoader,
                        {
                            token: undefined,
                            tokenPayload: undefined,
                            isLoggedIn: false,
                            username: undefined,
                            passwordChanged: false
                        });
                }

            case "login/change-password":
                {
                    const newPassword = action.newPassword;

                    if (!newPassword)
                    {
                        return stateLoader;
                    }

                    LoginDataManager.changePassword(newPassword);

                    return stateLoader;
                    //return stateLoader.setLoadObject(LoadObject.loading<IState>());
                }

            case "login/change-password/fulfilled":
                    {
                        const token = state.token;
                        const tokenPayload = state.tokenPayload;
                        const username = state.username;
                        const isLoggedIn = state.isLoggedIn;

                        return this.wrap(
                            stateLoader,
                            {
                                token,
                                tokenPayload,
                                isLoggedIn,
                                username,
                                passwordChanged: true
                            });
                    }
    
                case "login/change-password/error":
                    {
                        console.log(action.error);
                        return stateLoader.setLoadObject(stateLoader.getLoadObject().setError(action.error));
                    }

            default:
                return stateLoader;
        }
    }

    private unwrap(state: LoadObjectState<IState>): IState
    {
        const lo = state.getLoadObject();
        if (!lo)
        {
            return emptyState;
        }

        if (!lo.hasValue())
        {
            return emptyState;
        }

        return lo.getValue() || emptyState;
    }

    private wrap(loader: LoadObjectState<IState>, state: IState): LoadObjectState<IState>
    {
        return loader.setLoadObject(LoadObject.withValue(state));
    }

}

export default new LoginStore();
